import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Package, Folder, AlertCircle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const { token, selectedStore } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [selectedStore]);

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedStore) params.append('store_id', selectedStore);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/stats?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        toast.error('Failed to load dashboard stats');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="font-mono text-muted-foreground">Loading dashboard...</div>;
  }

  const totalProducts = stats ? stats.total_products : 0;
  const totalCategories = stats ? stats.total_categories : 0;
  const lowStockCount = stats ? stats.low_stock_count : 0;
  const lowStockVariants = stats && stats.low_stock_variants ? stats.low_stock_variants : [];

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight" data-testid="dashboard-title">Dashboard</h1>
        <p className="text-muted-foreground font-body mt-2">Overview of your inventory system</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors duration-300" data-testid="stat-card-total-products">
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Total Products</span>
            <Package className="text-blue-500" size={20} strokeWidth={1.5} />
          </div>
          <div className="font-mono text-3xl font-bold tracking-tight" data-testid="stat-value-0">{totalProducts}</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors duration-300" data-testid="stat-card-categories">
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Categories</span>
            <Folder className="text-green-500" size={20} strokeWidth={1.5} />
          </div>
          <div className="font-mono text-3xl font-bold tracking-tight" data-testid="stat-value-1">{totalCategories}</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors duration-300" data-testid="stat-card-low-stock-items">
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Low Stock Items</span>
            <AlertCircle className="text-red-500" size={20} strokeWidth={1.5} />
          </div>
          <div className="font-mono text-3xl font-bold tracking-tight" data-testid="stat-value-2">{lowStockCount}</div>
        </div>
      </div>

      {lowStockVariants.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6" data-testid="low-stock-alerts">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="text-destructive" size={20} strokeWidth={1.5} />
            <h2 className="font-heading font-bold text-2xl">Low Stock Variants</h2>
          </div>
          <div className="space-y-3">
            {lowStockVariants.map((variant, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-4 bg-secondary rounded-md border border-border"
                data-testid={`low-stock-variant-${idx}`}
              >
                <div className="flex-1">
                  <p className="font-body font-medium text-base">{variant.product_name}</p>
                  <p className="font-mono text-sm text-primary mt-1">{variant.variant_label}</p>
                </div>
                <div className="text-right">
                  <div className="font-mono font-semibold text-lg">{variant.quantity} units</div>
                  <div className="text-xs text-muted-foreground font-mono">Min: {variant.min_stock_level}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
